import { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Loader2, Paperclip, Upload, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import type { ChatAttachment, FiledDocument } from '../lib/types';

// The documents a conversation is about: uploaded straight in, or pointed at a
// version of something already filed in the area the person is on. Both end up
// as the same attachment row, so the agent reads them the same way.
//
// `useChatAttachments` holds them; `AttachButton` sits inside the message box
// and `AttachmentChips` just above it, so attaching reads as part of writing.

export function useChatAttachments(chatId: string | null, ensureChat: () => Promise<string | null>) {
    const { authFetch } = useAuth();
    const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async (cid: string) => {
        const res = await authFetch(`/agents/chats/${cid}/attachments`);
        if (res.ok) setAttachments(await res.json());
    }, [authFetch]);

    useEffect(() => {
        if (!chatId) { setAttachments([]); return; }
        load(chatId).catch(() => {});
    }, [chatId, load]);

    const run = async (act: (cid: string) => Promise<Response>, failure: string) => {
        setError(null);
        setBusy(true);
        try {
            const cid = chatId ?? await ensureChat();
            if (!cid) throw new Error('no chat');
            const res = await act(cid);
            if (!res.ok) throw new Error(failure);
            await load(cid);
        } catch {
            setError(failure);
        } finally {
            setBusy(false);
        }
    };

    const link = (f: FiledDocument) => run(cid => authFetch(`/agents/chats/${cid}/attachments/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            file_id: f.file_id, version: f.version, definition_id: f.definition_id, label: f.name,
        }),
    }), 'That document could not be attached.');

    const upload = (file: File) => {
        const form = new FormData();
        form.append('file', file);
        return run(cid => authFetch(`/agents/chats/${cid}/attachments`, { method: 'POST', body: form }),
            'That file could not be attached.');
    };

    const remove = (attachmentId: string) => run(cid => authFetch(
        `/agents/chats/${cid}/attachments/${attachmentId}`, { method: 'DELETE' },
    ), 'That attachment could not be removed.');

    return { attachments, busy, error, link, upload, remove };
}

type Attachments = ReturnType<typeof useChatAttachments>;

export function AttachButton({ state, filed }: { state: Attachments; filed: FiledDocument[] }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const fileInput = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;
        const onMouseDown = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const chooseFile = () => { setOpen(false); fileInput.current?.click(); };

    return (
        <div ref={ref} className="absolute left-1.5 bottom-1.5 z-10">
            <button
                type="button"
                onClick={() => (filed.length > 0 ? setOpen(o => !o) : chooseFile())}
                disabled={state.busy}
                aria-label="Attach a document"
                aria-haspopup={filed.length > 0 ? 'menu' : undefined}
                aria-expanded={filed.length > 0 ? open : undefined}
                title="Attach a document"
                className="w-7 h-7 grid place-items-center rounded-md text-coolslate hover:text-brand-dark hover:bg-offwhite transition-colors disabled:opacity-50"
            >
                {state.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
            </button>

            {open && (
                <div
                    role="menu"
                    className="absolute bottom-full left-0 mb-2 w-72 max-w-[calc(100vw-2rem)] max-h-72 overflow-y-auto navaris-dropdown z-30"
                >
                    {filed.map(f => (
                        <button
                            key={`${f.file_id}-${f.version}`}
                            type="button"
                            role="menuitem"
                            onClick={() => { setOpen(false); state.link(f); }}
                            className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-graphite hover:bg-offwhite"
                        >
                            <FileText className="w-4 h-4 shrink-0 text-coolslate" />
                            <span className="truncate">{f.name}{f.version ? ` v${f.version}` : ''}</span>
                        </button>
                    ))}
                    <div className="my-1 border-t border-softgrey/60" />
                    <button
                        type="button"
                        role="menuitem"
                        onClick={chooseFile}
                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-graphite hover:bg-offwhite"
                    >
                        <Upload className="w-4 h-4 shrink-0 text-coolslate" />
                        Upload a file
                    </button>
                </div>
            )}

            <input
                ref={fileInput}
                type="file"
                className="hidden"
                onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) state.upload(f);
                    e.target.value = '';
                }}
            />
        </div>
    );
}

export function AttachmentChips({ state }: { state: Attachments }) {
    if (state.attachments.length === 0 && !state.error) return null;
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {state.attachments.map(a => {
                const label = a.label || a.filename || 'Document';
                return (
                    <span
                        key={a.id}
                        className="inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded-full bg-offwhite border border-softgrey text-xs text-graphite"
                    >
                        <Paperclip className="w-3 h-3 shrink-0 text-coolslate" />
                        <span className="truncate">{label}{a.version ? ` v${a.version}` : ''}</span>
                        <button
                            onClick={() => state.remove(a.id)}
                            disabled={state.busy}
                            aria-label={`Remove ${label}`}
                            className="text-coolslate hover:text-graphite disabled:opacity-50"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                );
            })}
            {state.error && <span className="text-xs text-state-error">{state.error}</span>}
        </div>
    );
}
